'use client'

import * as React from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { DayPicker } from 'react-day-picker'
import { nl } from 'date-fns/locale'
import { cn } from '../lib/utils'
import { buttonVariants } from './button'

export type CalendarProps = React.ComponentProps<typeof DayPicker> & {
  /**
   * Toon ISO-weeknummers links van de dagen. Default `false` zodat
   * bestaande callers ongewijzigd blijven; werkbon-create-modal en andere
   * planner-flows zetten dit op `true`.
   */
  showWeekNumbers?: boolean
}

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  showWeekNumbers = false,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      locale={nl}
      weekStartsOn={1}
      ISOWeek
      showOutsideDays={showOutsideDays}
      showWeekNumber={showWeekNumbers}
      className={cn('p-3', className)}
      classNames={{
        months: 'flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0',
        month: 'space-y-4',
        caption: 'flex justify-center pt-1 relative items-center',
        caption_label: 'text-sm font-medium capitalize',
        nav: 'space-x-1 flex items-center',
        nav_button: cn(
          buttonVariants({ variant: 'outline' }),
          'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100',
        ),
        nav_button_previous: 'absolute left-1',
        nav_button_next: 'absolute right-1',
        table: 'w-full border-collapse space-y-1',
        head_row: 'flex',
        head_cell: 'text-[#5C5C58] rounded-md w-9 font-normal text-[0.8rem]',
        row: 'flex w-full mt-2',
        cell: 'h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected])]:bg-[#F0F1EC] first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20',
        weeknumber: 'h-9 w-8 text-center text-[0.7rem] p-0 text-[#5C5C58] tabular-nums opacity-70 select-none',
        day: cn(
          buttonVariants({ variant: 'ghost' }),
          'h-9 w-9 p-0 font-normal aria-selected:opacity-100',
        ),
        day_range_end: 'day-range-end',
        day_selected:
          'bg-[#CCFA78] text-[#1A1A18] hover:bg-[#CCFA78] hover:text-[#1A1A18] focus:bg-[#CCFA78] focus:text-[#1A1A18]',
        day_today: 'bg-[#F0F1EC] text-[#1A1A18] font-semibold',
        day_outside:
          'day-outside text-[#5C5C58] opacity-50 aria-selected:bg-[#F0F1EC]/50 aria-selected:text-[#5C5C58] aria-selected:opacity-30',
        day_disabled: 'text-[#5C5C58] opacity-50',
        day_range_middle: 'aria-selected:bg-[#F0F1EC] aria-selected:text-[#1A1A18]',
        day_hidden: 'invisible',
        caption_dropdowns: 'flex gap-1 items-center justify-center',
        dropdown:
          'rounded-md border border-[#CED0C8] bg-white px-2 py-1 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#CCFA78]',
        dropdown_month: '',
        dropdown_year: '',
        vhidden: 'sr-only',
        ...classNames,
      }}
      components={{
        IconLeft: () => <ChevronLeft className="h-4 w-4" />,
        IconRight: () => <ChevronRight className="h-4 w-4" />,
      }}
      {...props}
    />
  )
}
Calendar.displayName = 'Calendar'

export { Calendar }
